import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search, X, AlertCircle, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

export interface EntityPickerOption {
  id: number;
  label: string;
  description?: string;
}

interface EntityPickerProps {
  /** Currently selected id. null = nothing selected. */
  value: number | null;
  /** Fires when the user picks an option or clears. */
  onChange: (id: number | null) => void;
  /** React-query key for caching the options list. */
  queryKey: readonly unknown[];
  /** Loader returning the full list of options (client-side filter). */
  loadOptions: () => Promise<EntityPickerOption[]>;
  placeholder?: string;
  emptyMessage?: string;
  /** Danger border + aria-invalid. */
  invalid?: boolean;
  /** Shows an X to clear selection. Defaults true. */
  allowClear?: boolean;
  /** Disable interaction. */
  disabled?: boolean;
  /** Small note rendered inside the dropdown footer (e.g., "Only local templates shown"). */
  footerNote?: string;
  /** aria-label for the input (for accessibility when there's no visible label). */
  ariaLabel?: string;
}

/**
 * Generic searchable entity picker used wherever a form field is an FK to
 * another entity (templates, workflows, daemons, etc). Client-side filters a
 * fetched list — suitable up to a few hundred items per org. When a list
 * outgrows that, swap the internal filter for server-side search.
 */
export const EntityPicker: React.FC<EntityPickerProps> = ({
  value,
  onChange,
  queryKey,
  loadOptions,
  placeholder = 'Select...',
  emptyMessage = 'No options available',
  invalid = false,
  allowClear = true,
  disabled = false,
  footerNote,
  ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    data: options = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey,
    queryFn: loadOptions,
    staleTime: 60_000,
  });

  const selected = useMemo(() => options.find((o) => o.id === value) ?? null, [options, value]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q) || o.description?.toLowerCase().includes(q));
  }, [options, search]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    if (open) setActiveIndex(0);
  }, [open, search]);

  const openPanel = () => {
    if (disabled) return;
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const pick = (id: number) => {
    onChange(id);
    setOpen(false);
    setSearch('');
  };

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setSearch('');
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(0, filtered.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[activeIndex];
      if (opt) pick(opt.id);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  const triggerLabel = selected?.label ?? (value != null && !isLoading ? `(id: ${value})` : null);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : openPanel())}
        aria-invalid={invalid || undefined}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        className={`w-full flex items-center gap-2 px-3 py-2 bg-surface-alt border rounded-lg text-sm text-left transition ${invalid ? 'border-danger focus:ring-danger' : 'focus:ring-accent'} focus:outline-hidden focus:ring-1 ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-strong'}`}
      >
        <span className={`flex-1 truncate ${triggerLabel ? 'text-body' : 'text-faint'}`}>
          {triggerLabel ?? placeholder}
        </span>
        {allowClear && value != null && !disabled && (
          <span
            role="button"
            aria-label="Clear selection"
            onClick={clear}
            className="p-0.5 rounded-sm hover:bg-surface-hover text-faint hover:text-danger"
          >
            <X size={14} />
          </span>
        )}
        <ChevronDown size={14} className="text-faint shrink-0" />
      </button>

      {open && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-surface border rounded-lg shadow-lg overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b">
            <Search size={14} className="text-faint shrink-0" />
            <input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Search..."
              className="flex-1 bg-transparent outline-hidden text-sm text-body placeholder:text-faint"
            />
          </div>
          <ul role="listbox" className="max-h-60 overflow-y-auto">
            {isLoading && (
              <li className="flex items-center gap-2 px-3 py-3 text-sm text-muted">
                <Loader2 size={14} className="animate-spin" /> Loading...
              </li>
            )}
            {isError && (
              <li className="flex items-center gap-2 px-3 py-3 text-sm text-danger">
                <AlertCircle size={14} /> Failed to load options
              </li>
            )}
            {!isLoading && !isError && filtered.length === 0 && (
              <li className="px-3 py-3 text-sm text-muted">{emptyMessage}</li>
            )}
            {!isLoading &&
              !isError &&
              filtered.map((opt, i) => {
                const isActive = i === activeIndex;
                const isSelected = opt.id === value;
                return (
                  <li
                    key={opt.id}
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => setActiveIndex(i)}
                    onClick={() => pick(opt.id)}
                    className={`flex items-start gap-2 px-3 py-2 cursor-pointer text-sm ${isActive ? 'bg-surface-hover' : ''}`}
                  >
                    <Check size={14} className={`mt-0.5 shrink-0 ${isSelected ? 'text-accent-text' : 'invisible'}`} />
                    <span className="flex-1 min-w-0">
                      <span className="block text-heading truncate">{opt.label}</span>
                      {opt.description && <span className="block text-xs text-faint truncate">{opt.description}</span>}
                    </span>
                  </li>
                );
              })}
          </ul>
          {footerNote && <div className="px-3 py-2 text-xs text-faint border-t bg-surface-alt">{footerNote}</div>}
        </div>
      )}
    </div>
  );
};
